/* eslint-disable react/jsx-pascal-case */
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { 
    M_Button, 
    M_MessageBoard,
    BufferService, 
    LoadingContext 
} from 'src/M_Components'
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Card,
    CardContent,
    IconButton,
    Divider,
    Box,
    Typography,
    Grid
} from '@mui/material'
import {
    Add,
    Delete,
    Edit,
    Save,
    Cancel,
    ExpandMore,
    Visibility,
    Publish
} from '@mui/icons-material'
import './pages.css'
import './ChangeLogAdminPage.css'

export default function ChangeLogAdminPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const ctx = useContext(LoadingContext)

    // Services
    const logChangeService = new BufferService('logChange')
    const stateFeatureService = new BufferService('stateFeature')
    const userService = new BufferService('identity/userrole')

    // User info
    // eslint-disable-next-line no-unused-vars
    const [user, setUser] = useState(null)

    // Form state
    const [version, setVersion] = useState('')
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [releaseDate, setReleaseDate] = useState(new Date().toISOString().split('T')[0])
    const [isPublished, setIsPublished] = useState(false)

    // Features management
    const [features, setFeatures] = useState([])
    const [currentFeature, setCurrentFeature] = useState({
        idStateFeature: '',
        title: '',
        description: ''
    })
    const [editingFeatureIndex, setEditingFeatureIndex] = useState(null)

    // Available states
    const [stateFeatures, setStateFeatures] = useState([])

    // Existing versions
    const [existingVersions, setExistingVersions] = useState([])

    // Validation
    const [errors, setErrors] = useState({})
    const [showPreview, setShowPreview] = useState(false)
    const [expandedVersion, setExpandedVersion] = useState(null)
    const [selectedVersionId, setSelectedVersionId] = useState(null)
    const [openPublishConfirm, setOpenPublishConfirm] = useState(false)
    const [createAccordionExpanded, setCreateAccordionExpanded] = useState(false)
    const [publishingVersionId, setPublishingVersionId] = useState(null)

    // Handle API errors with specific messages based on error code
    const handleApiError = (error) => {
        // Check if error response contains structured error information
        if (error.response?.data) {
            const errorData = error.response.data
            
            // Check for specific error codes from backend
            if (errorData.ErrorCode) {
                switch (errorData.ErrorCode) {
                    case 'DUPLICATE_VERSION':
                        const duplicateVersion = errorData.Context?.Version || version
                        ctx.toastMessages('error', '', t('ErrorDuplicateVersion', { version: duplicateVersion }))
                        break
                    
                    case 'INVALID_VERSION_FORMAT':
                        ctx.toastMessages('error', '', t('ErrorInvalidVersionFormat'))
                        break
                    
                    case 'EMPTY_FEATURES':
                        ctx.toastMessages('error', '', t('ErrorEmptyFeatures'))
                        break
                    
                    default:
                        // Use first error message from Errors array
                        const message = errorData.Errors?.[0] || t('ErrorSavingVersion')
                        ctx.toastMessages('error', '', message)
                }
            } else if (errorData.Errors && errorData.Errors.length > 0) {
                // Show first error message
                ctx.toastMessages('error', '', errorData.Errors[0])
            } else if (errorData.message) {
                ctx.toastMessages('error', '', errorData.message)
            } else {
                ctx.toastMessages('error', '', t('ErrorSavingVersion'))
            }
        } else if (error.message) {
            ctx.toastMessages('error', '', error.message)
        } else {
            ctx.toastMessages('error', '', t('ErrorSavingVersion'))
        }
    }

    useEffect(() => {
        ctx.setIsLoad(true)
        userService.get().then(res => {
            if (res.name === 'AxiosError') {
                navigate('/unauthorized')
                return
            }

            const isSuperAdmin = res.Role?.some(role => role.Name === 'SuperAdmin')
            const isAdmin = res.Role?.some(role => role.Name === 'Admin')
            const canManage = isSuperAdmin || isAdmin
            
            if (!canManage) {
                navigate('/unauthorized')
                return
            }

            setUser(res)
            loadInitialData()
        }).catch(() => {
            navigate('/unauthorized')
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loadInitialData = async () => {
        try {
            // Load state features (types) with colors
            const statesResponse = await stateFeatureService.filter('/all-with-colors')
            if (statesResponse && Array.isArray(statesResponse)) {
                setStateFeatures(statesResponse)
            } else if (statesResponse.Items) {
                setStateFeatures(statesResponse.Items)
            }

            // Load existing versions
            const versionsResponse = await logChangeService.get()
            if (versionsResponse.Items) {
                setExistingVersions(versionsResponse.Items)
            }

            ctx.setIsLoad(false)
        } catch (error) {
            console.error('Error loading data:', error)
            ctx.toastMessages('error', '', t('ErrorLoadingData'))
            ctx.setIsLoad(false)
        }
    }

    const validateVersion = (versionString) => {
        const versionRegex = /^\d+\.\d+\.\d+(-[a-zA-Z0-9]+)?$/
        return versionRegex.test(versionString)
    }

    const validateForm = () => {
        const newErrors = {}

        if (!version) {
            newErrors.version = t('VersionRequired')
        } else if (!validateVersion(version)) {
            newErrors.version = t('InvalidVersionFormat')
        } else {
            // Check if version already exists (when creating new or changing version)
            const isDuplicateVersion = existingVersions.some(v => 
                v.Version === version && v.Id !== selectedVersionId
            )
            if (isDuplicateVersion) {
                newErrors.version = t('VersionAlreadyExists')
            }
        }

        if (!title) {
            newErrors.title = t('TitleRequired')
        }

        if (!description) {
            newErrors.description = t('DescriptionRequired')
        }

        if (features.length === 0) {
            newErrors.features = t('AtLeastOneFeatureRequired')
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const addOrUpdateFeature = () => {
        if (!currentFeature.idStateFeature || !currentFeature.title) {
            return
        }

        if (editingFeatureIndex !== null) {
            const updatedFeatures = [...features]
            updatedFeatures[editingFeatureIndex] = currentFeature
            setFeatures(updatedFeatures)
            setEditingFeatureIndex(null)
        } else {
            setFeatures([...features, currentFeature])
        }

        setCurrentFeature({ idStateFeature: '', title: '', description: '' })
    }

    const editFeature = (index) => {
        setCurrentFeature(features[index])
        setEditingFeatureIndex(index)
    }

    const deleteFeature = (index) => {
        setFeatures(features.filter((_, i) => i !== index))
    }

    const cancelEditFeature = () => {
        setCurrentFeature({ idStateFeature: '', title: '', description: '' })
        setEditingFeatureIndex(null)
    }

    const saveChangeLog = async () => {
        if (!validateForm()) {
            return
        }

        // Frontend validation: Check if features list is empty
        if (features.length === 0) {
            ctx.toastMessages('warning', '', t('PleaseAddFeatures'))
            return
        }

        ctx.setIsLoad(true)
        const payload = {
            Version: version,
            Title: title,
            Description: description,
            ReleaseDate: releaseDate + 'T00:00:00',
            IsPublished: isPublished,
            LogFeatureList: features.map(f => ({
                idStateFeature: f.idStateFeature,
                Title: f.title,
                Description: f.description
            }))
        }

        try {
            let response
            if (selectedVersionId) {
                // Update existing version
                response = await logChangeService.update(selectedVersionId, payload)
            } else {
                // Create new version
                response = await logChangeService.post(payload)
            }
            
            if (response.name === 'AxiosError') {
                handleApiError(response)
                ctx.setIsLoad(false)
                return
            }
            
            ctx.toastMessages('success', '', t('VersionSavedSuccessfully'))
            resetForm()
            loadInitialData()
        } catch (error) {
            ctx.toastMessages('error', '', error.message)
            ctx.setIsLoad(false)
        }
    }

    const publishExistingVersion = (versionData) => {
        setPublishingVersionId(versionData.Id)
        setVersion(versionData.Version)
        setTitle(versionData.Title)
        setDescription(versionData.Description)
        setOpenPublishConfirm(true)
    }

    const confirmPublishExisting = async () => {
        setOpenPublishConfirm(false)
        ctx.setIsLoad(true)
        
        try {
            // Call publish endpoint
            const response = await logChangeService.update(`${publishingVersionId}/publish`, { IsPublished: true })
            
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', '', response.message)
                ctx.setIsLoad(false)
                return
            }
            
            ctx.toastMessages('success', '', t('VersionPublishedSuccessfully'))
            setPublishingVersionId(null)
            loadInitialData()
        } catch (error) {
            ctx.toastMessages('error', '', error.message)
            ctx.setIsLoad(false)
        }
    }

    const publishChangeLog = () => {
        if (!validateForm()) {
            return
        }
        setOpenPublishConfirm(true)
    }

    const confirmPublish = async () => {
        if (publishingVersionId) {
            confirmPublishExisting()
        } else {
            setOpenPublishConfirm(false)
            ctx.setIsLoad(true)
            const payload = {
                Version: version,
                Title: title,
                Description: description,
                ReleaseDate: releaseDate + 'T00:00:00',
                IsPublished: isPublished,
                LogFeatureList: features.map(f => ({
                    idStateFeature: f.idStateFeature,
                    Title: f.title,
                    Description: f.description
                }))
            }
            
            try {
                const response = await logChangeService.post(payload)
                
                if (response.name === 'AxiosError') {
                    ctx.toastMessages('error', '', response.message)
                    ctx.setIsLoad(false)
                    return
                }
                
                ctx.toastMessages('success', '', t('VersionPublishedSuccessfully'))
                resetForm()
                loadInitialData()
            } catch (error) {
                ctx.toastMessages('error', '', error.message)
                ctx.setIsLoad(false)
            }
        }
    }

    const loadVersion = (versionData) => {
        setVersion(versionData.Version)
        setTitle(versionData.Title)
        setDescription(versionData.Description)
        setReleaseDate(versionData.ReleaseDate ? versionData.ReleaseDate.split('T')[0] : new Date().toISOString().split('T')[0])
        setIsPublished(versionData.IsPublished)
        setFeatures(versionData.LogFeatureList.map(f => ({
            idStateFeature: stateFeatures.find(sf => sf.Name === f.Feature)?.Id || '',
            title: f.Title,
            description: f.Description || ''
        })))
        setSelectedVersionId(versionData.Id)
        setExpandedVersion(null)
        setCreateAccordionExpanded(true)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const resetForm = () => {
        setVersion('')
        setTitle('')
        setDescription('')
        setReleaseDate(new Date().toISOString().split('T')[0])
        setIsPublished(false)
        setFeatures([])
        setCurrentFeature({ idStateFeature: '', title: '', description: '' })
        setEditingFeatureIndex(null)
        setErrors({})
        setShowPreview(false)
        setSelectedVersionId(null)
        setCreateAccordionExpanded(false)
    }

    const deleteVersion = async (versionId) => {
        if (window.confirm(t('ConfirmDeleteVersion'))) {
            ctx.setIsLoad(true)
            try {
                const response = await logChangeService.delete(versionId)
                
                if (response.name === 'AxiosError') {
                    ctx.toastMessages('error', '', response.message)
                    ctx.setIsLoad(false)
                    return
                }
                
                ctx.toastMessages('success', '', t('VersionDeletedSuccessfully'))
                loadInitialData()
            } catch (error) {
                ctx.toastMessages('error', '', error.message)
                ctx.setIsLoad(false)
            }
        }
    }

    return (
        <div className="changelog-admin-container">
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#333' }}>
                {t('ManageChangelog')}
            </Typography>
            {/* Create/Edit Version Accordion */}
            <Accordion 
                expanded={createAccordionExpanded}
                onChange={(e, isExpanded) => setCreateAccordionExpanded(isExpanded)}
                sx={{ mb: 3, boxShadow: 2 }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{ 
                        backgroundColor: '#f9f9f9',
                        '&:hover': { backgroundColor: '#f0f0f0' },
                        borderRadius: '4px'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Add sx={{ color: '#1976d2' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                            {t('CreateNewVersion')}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ p: 2 }}>
                        {/* Version Info */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('Version')}
                                    value={version}
                                    onChange={(e) => setVersion(e.target.value)}
                                    placeholder="1.0.0"
                                    error={!!errors.version}
                                    helperText={errors.version || t('SemanticVersioningFormat')}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('Title')}
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={t('VersionTitle')}
                                    error={!!errors.title}
                                    helperText={errors.title}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label={t('VersionDescription')}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t('VersionDescriptionPlaceholder')}
                                    error={!!errors.description}
                                    helperText={errors.description}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t('ReleaseDate')}
                                    type="date"
                                    value={releaseDate}
                                    onChange={(e) => setReleaseDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        {/* Features Section */}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, color: '#1976d2' }}>
                            {t('Features')}
                        </Typography>

                        {errors.features && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {errors.features}
                            </Alert>
                        )}

                        {/* Add Feature Form */}
                        <Card variant="outlined" sx={{ mb: 3, backgroundColor: '#f9f9f9' }}>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>{t('FeatureType')}</InputLabel>
                                            <Select
                                                value={currentFeature.idStateFeature}
                                                onChange={(e) => setCurrentFeature({
                                                    ...currentFeature,
                                                    idStateFeature: e.target.value
                                                })}
                                                label={t('FeatureType')}
                                            >
                                                {stateFeatures.map(sf => (
                                                    <MenuItem key={sf.Id} value={sf.Id}>
                                                        <Chip
                                                            label={sf.Name}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: sf.ColorFeature?.Code || '#ccc',
                                                                color: '#fff',
                                                                fontSize: '0.7rem'
                                                            }}
                                                        />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={9}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={t('FeatureTitle')}
                                            value={currentFeature.title}
                                            onChange={(e) => setCurrentFeature({
                                                ...currentFeature,
                                                title: e.target.value
                                            })}
                                            placeholder={t('FeatureTitlePlaceholder')}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={2}
                                            size="small"
                                            label={t('FeatureDescription') + ' (' + t('Optional') + ')'}
                                            value={currentFeature.description}
                                            onChange={(e) => setCurrentFeature({
                                                ...currentFeature,
                                                description: e.target.value
                                            })}
                                            placeholder={t('FeatureDescriptionPlaceholder')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                        <M_Button
                                            icon={editingFeatureIndex !== null ? <Save /> : <Add />}
                                            label={editingFeatureIndex !== null ? t('Update') : t('Add')}
                                            onClick={addOrUpdateFeature}
                                            disabled={!currentFeature.idStateFeature || !currentFeature.title}
                                        />
                                        {editingFeatureIndex !== null && (
                                            <M_Button
                                                icon={<Cancel />}
                                                label={t('Cancel')}
                                                onClick={cancelEditFeature}
                                                hoverEffect
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Features List */}
                        {features.length > 0 ? (
                            <Box sx={{ mb: 3 }}>
                                {features.map((feature, index) => {
                                    const state = stateFeatures.find(s => s.Id === feature.idStateFeature)
                                    return (
                                        <Card key={index} variant="outlined" sx={{ mb: 1 }}>
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                    <Chip
                                                        label={state?.Name}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: state?.ColorFeature?.Code || '#ccc',
                                                            color: '#fff',
                                                            fontSize: '0.7rem',
                                                            fontWeight: 600,
                                                            minWidth: '100px'
                                                        }}
                                                    />
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                            {feature.title}
                                                        </Typography>
                                                        {feature.description && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {feature.description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <IconButton size="small" onClick={() => editFeature(index)} color="primary">
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" onClick={() => deleteFeature(index)} color="error">
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    )
                                })}
                            </Box>
                        ) : (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                {t('NoFeaturesAdded')}
                            </Alert>
                        )}

                        <Divider sx={{ my: 3 }} />

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <M_Button
                                icon={<Visibility />}
                                label={t('Preview')}
                                onClick={() => setShowPreview(!showPreview)}
                                disabled={features.length === 0}
                            />
                            <M_Button
                                icon={<Save />}
                                label={t('Save')}
                                onClick={saveChangeLog}
                            />
                            <M_Button
                                icon={<Publish />}
                                label={t('Publish')}
                                onClick={publishChangeLog}
                            />
                            <M_Button
                                icon={<Cancel />}
                                label={t('Clear')}
                                onClick={resetForm}
                                hoverEffect
                            />
                        </Box>

                        {/* Preview */}
                        {showPreview && (
                            <Box sx={{ mt: 3 }}>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                                    <Visibility sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    {t('Preview')}
                                </Typography>
                                <Card sx={{ backgroundColor: '#f5f5f5', p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Chip
                                            label={`v${version}`}
                                            color="primary"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                        <Typography variant="h5" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                            {title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" sx={{ mb: 1, color: '#555' }}>
                                        {description}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 3, color: '#999' }}>
                                        {t('ReleaseDate')}: {new Date(releaseDate).toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                        {t('Features')}:
                                    </Typography>
                                    {features.map((feature, index) => {
                                        const state = stateFeatures.find(s => s.Id === feature.idStateFeature)
                                        return (
                                            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                                                <Chip
                                                    label={state?.Name}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: state?.ColorFeature?.Code || '#ccc',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 600
                                                    }}
                                                />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {feature.title}
                                                    </Typography>
                                                    {feature.description && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {feature.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        )
                                    })}
                                </Card>
                            </Box>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Existing Versions */}
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                {t('ExistingVersions')}
            </Typography>

            {existingVersions.map((versionData, index) => (
                <Accordion
                    key={versionData.Id}
                    expanded={expandedVersion === versionData.Id}
                    onChange={() => setExpandedVersion(expandedVersion === versionData.Id ? null : versionData.Id)}
                    sx={{ mb: 2, boxShadow: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                            <Chip
                                label={`v${versionData.Version}`}
                                color="primary"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                            />
                            <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
                                {versionData.Title}
                            </Typography>
                            <Chip
                                label={versionData.IsPublished ? t('Published') : t('Draft')}
                                color={versionData.IsPublished ? 'success' : 'default'}
                                size="small"
                            />
                            <Typography variant="body2" color="text.secondary">
                                {new Date(versionData.ReleaseDate).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
                                {versionData.Description}
                            </Typography>

                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2 }}>
                                {t('Features')}:
                            </Typography>

                            {versionData.LogFeatureList.map((feature, fIndex) => (
                                <Card key={fIndex} variant="outlined" sx={{ mb: 1, backgroundColor: '#f9f9f9' }}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Chip
                                                label={feature.Feature}
                                                size="small"
                                                sx={{
                                                    backgroundColor: feature.Color,
                                                    color: '#fff',
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600,
                                                    minWidth: '100px'
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                    {feature.Title}
                                                </Typography>
                                                {feature.Description && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {feature.Description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <M_Button
                                    icon={<Edit />}
                                    label={t('Edit')}
                                    onClick={() => loadVersion(versionData)}
                                />
                                {!versionData.IsPublished && (
                                    <M_Button
                                        icon={<Publish />}
                                        label={t('Publish')}
                                        onClick={() => publishExistingVersion(versionData)}
                                    />
                                )}
                                <M_Button
                                    icon={<Delete />}
                                    label={t('Delete')}
                                    onClick={() => deleteVersion(versionData.Id)}
                                    hoverEffect
                                />
                            </Box>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}

            {existingVersions.length === 0 && (
                <Alert severity="info">
                    {t('NoVersionsFound')}
                </Alert>
            )}

            {/* Publish Confirmation Modal */}
            <M_MessageBoard
                open={openPublishConfirm}
                minWidth={'400px'}
                onClose={() => setOpenPublishConfirm(false)}
                title={t('ConfirmPublishVersion')}
                warning={true}
                body={
                    <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            <Chip label={`v${version}`} size="small" color="primary" sx={{ mr: 1 }} />
                            {title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {description}
                        </Typography>
                    </Box>
                }
                footer={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <M_Button
                            hoverEffect
                            normal
                            labelColor={'white'}
                            backgroundColor={'var(--color-red)'}
                            onClick={() => setOpenPublishConfirm(false)}
                            label={t('Cancel')}
                        />
                        <M_Button
                            hoverEffect
                            normal
                            labelColor={'white'}
                            backgroundColor={'var(--color-green)'}
                            onClick={confirmPublish}
                            label={t('Confirm')}
                            icon={<Publish />}
                        />
                    </Box>
                }
            />
        </div>
    )
}
